import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Sun, Moon, CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import logoPresida from '../../images/logo-presida-vertical.png';
import bgImage from '../../images/presida_BG.jpg';
import { supabase } from '../../lib/supabase';

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error checking session:', error);
          setIsValidToken(false);
          setError('Link de recuperação inválido ou expirado. Solicite um novo link.');
        } else if (session) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
          setError('Link de recuperação inválido ou expirado. Solicite um novo link.');
        }
      } catch (err) {
        console.error('Unexpected error checking token:', err);
        setIsValidToken(false);
        setError('Erro ao verificar link de recuperação.');
      } finally {
        setCheckingToken(false);
      }
    };

    checkSession();
  }, []);

  const validatePassword = (pwd: string): { isValid: boolean; message: string } => {
    if (pwd.length < 8) {
      return { isValid: false, message: 'A senha deve ter no mínimo 8 caracteres' };
    }
    if (!/[A-Z]/.test(pwd)) {
      return { isValid: false, message: 'A senha deve conter pelo menos uma letra maiúscula' };
    }
    if (!/[a-z]/.test(pwd)) {
      return { isValid: false, message: 'A senha deve conter pelo menos uma letra minúscula' };
    }
    if (!/[0-9]/.test(pwd)) {
      return { isValid: false, message: 'A senha deve conter pelo menos um número' };
    }
    return { isValid: true, message: 'Senha forte' };
  };

  const passwordValidation = password ? validatePassword(password) : null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Error updating password:', updateError);
        if (updateError.message.includes('session')) {
          setError('Sessão expirada. Por favor, solicite um novo link de recuperação.');
        } else {
          setError('Erro ao redefinir senha. Tente novamente.');
        }
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      console.error('Unexpected error during password reset:', err);
      setError('Erro inesperado ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="card p-8 w-full max-w-md relative z-10 bg-white/95 dark:bg-dark-paper/95 backdrop-blur-sm">
          <div className="flex flex-col items-center">
            <img src={logoPresida} alt="Presida.Club" className="h-24 w-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Verificando link de recuperação...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="card p-8 w-full max-w-md relative z-10 bg-white/95 dark:bg-dark-paper/95 backdrop-blur-sm">
          <div className="flex flex-col items-center mb-6">
            <img src={logoPresida} alt="Presida.Club" className="h-24 w-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Link Inválido</h2>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg mb-4 text-sm">
            {error}
          </div>
          <button
            onClick={() => navigate('/esqueci-senha')}
            className="btn-primary w-full"
          >
            Solicitar Novo Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/50" />

      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="card p-8 w-full max-w-md relative z-10 bg-white/95 dark:bg-dark-paper/95 backdrop-blur-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={logoPresida} alt="Presida.Club" className="h-24 w-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Redefinir Senha</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-center">
            {success ? 'Senha alterada com sucesso!' : 'Escolha uma nova senha forte para sua conta'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-lg text-sm">
            <p className="font-medium mb-2">Senha alterada com sucesso!</p>
            <p>Você será redirecionado para a página de login em instantes...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input pl-10 pr-10"
                  placeholder="********"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {passwordValidation && password && (
                <div className={`mt-2 text-sm flex items-center gap-1 ${passwordValidation.isValid ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  {passwordValidation.isValid ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {passwordValidation.message}
                </div>
              )}
            </div>

            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="input pl-10 pr-10"
                  placeholder="********"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <div className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  As senhas não coincidem
                </div>
              )}
              {confirmPassword && password === confirmPassword && (
                <div className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  As senhas coincidem
                </div>
              )}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? 'Redefinindo...' : 'Redefinir Senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
